import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { IntercomProvider, useIntercom } from 'react-use-intercom';

import {
  isIntercomBooted,
  markIntercomBooted,
  onIntercomBoot,
} from '../utils/intercomBootState';
import {
  addIntercomHideReason,
  isIntercomLauncherHidden,
  onIntercomHideReasonsChange,
  removeIntercomHideReason,
} from '../utils/intercomLauncherVisibility';

export const CustomIntercomStateContext = createContext<{ isOpen: boolean }>({
  isOpen: false,
});

export const useIsIntercomOpen = () => {
  const { isOpen } = useContext(CustomIntercomStateContext);

  return {
    isOpen,
  };
};

/**
 * Hides the Intercom launcher for as long as the calling component is
 * mounted (designer, live-hosting view, live-game viewer, game preview —
 * anywhere the bubble would sit on top of a full-screen surface).
 *
 * This only registers a reason with `utils/intercomLauncherVisibility` — it
 * doesn't call `update()` itself. That matters because more than one thing
 * can want the launcher hidden at once (this hook, and separately the
 * org/plan rule in `useIdentifyMetrics`): if this hook called `update()`
 * directly, its cleanup firing (e.g. leaving the designer) would reveal the
 * launcher even if the org/plan rule still wants it hidden.
 * `IntercomLauncherVisibilityController` is the one place that actually
 * calls `update()`, from the combined state.
 */
export const useHideIntercom = () => {
  useEffect(() => {
    const reason = Symbol('useHideIntercom');
    addIntercomHideReason(reason);
    return () => removeIntercomHideReason(reason);
  }, []);
};

/**
 * The single place that applies `hideDefaultLauncher` to the real widget,
 * from the combined state in `utils/intercomLauncherVisibility` — reasons
 * only ever register/unregister themselves there (see `useHideIntercom`
 * above and `useIdentifyMetrics`); nothing else calls `update()` for this.
 *
 * Also re-applies on boot: `update()` silently no-ops on a widget that isn't
 * booted yet, so a hide reason that becomes active before boot has happened
 * (the common case — boot only happens once an org/plan check or a delayed
 * anonymous timer resolves) would otherwise be dropped, and the launcher
 * would flash visible the moment boot does happen.
 *
 * Mounted once, for the app's whole lifetime, inside `CustomIntercomStateProvider`
 * below — not tied to route or auth state, since both hide-reason sources
 * can register/unregister independently of it.
 */
const IntercomLauncherVisibilityController = () => {
  const { update } = useIntercom();

  useEffect(() => {
    const apply = () => update({ hideDefaultLauncher: isIntercomLauncherHidden() });

    const unsubscribeHideReasons = onIntercomHideReasonsChange(apply);
    const unsubscribeBoot = onIntercomBoot(apply);

    return () => {
      unsubscribeHideReasons();
      unsubscribeBoot();
    };
  }, [update]);

  return null;
};

// How long an anonymous visitor waits before Intercom boots, mirroring the
// delay the old `autoBoot`/`initializeDelay` pair used to apply — so a
// marketing-page load still isn't blocked loading Intercom's script.
const ANONYMOUS_BOOT_DELAY_MS = 5000;

/**
 * Boots Intercom, with no identity, after a short delay — for visitors who
 * never go through the logged-in identify flow (`useIdentifyMetrics`), which
 * is the only other caller of `boot` and only mounts once
 * `useConfirmedSession` reports `hasBeenAuthenticated` (see
 * `BackendServiceProvider`). The two are rendered as mutually exclusive
 * siblings there, gated on that sticky flag rather than a raw
 * `session.data?.user?.uid` check — a transient `/api/auth/session` fetch
 * failure clears the uid without meaning the user signed out, and mounting
 * this component during that blip would boot Intercom anonymously moments
 * before the identify flow would otherwise have booted it with identity. So
 * at most one of the two ever calls `boot` for a given page load, and never
 * as a side effect of a session flap.
 *
 * This replaces the previous `autoBoot` prop on `IntercomProvider`. That
 * boot was unconditional and un-trackable from outside the provider, which
 * made it impossible for anything else to reliably know "is it actually
 * booted right now" — `markIntercomBooted`/`isIntercomBooted` fix that by
 * giving every caller a shared, accurate answer instead of each guessing
 * from elapsed time.
 *
 * Route- and org-agnostic on purpose: nothing here ever shuts Intercom back
 * down or withholds the boot — see `utils/intercomLauncherVisibility.ts` for
 * why the org/plan rule and route-based hiding are launcher-visibility
 * concerns, not boot-lifecycle ones (`trackEvent` and explicit "Chat with
 * us" entry points need the widget booted regardless of either).
 */
export const AnonymousIntercomAutoBoot = () => {
  const { boot } = useIntercom();

  useEffect(() => {
    // No extra "already scheduled" ref guard here on purpose: this effect's
    // cleanup fully undoes it (clearTimeout), so it's safe for the effect to
    // rerun and reschedule — which is exactly what Strict Mode's dev-only
    // mount → cleanup → mount cycle does (`reactStrictMode: true` in
    // next.config.js). A guard ref that's set here and never cleared by the
    // cleanup survives that cycle and permanently skips the second
    // scheduling, silently dropping Intercom for every anonymous visitor in
    // dev — that was the bug here before.
    const timer = setTimeout(() => {
      if (!isIntercomBooted()) {
        boot();
        markIntercomBooted();
      }
    }, ANONYMOUS_BOOT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [boot]);

  return null;
};

export const CustomIntercomStateProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const providerState = useMemo(() => ({ isOpen }), [isOpen]);

  const onHide = useCallback(() => {
    setIsOpen(false);
  }, []);
  const onShow = useCallback(() => {
    setIsOpen(true);
  }, []);

  return (
    <IntercomProvider
      appId={'lysrkvrj'}
      apiBase="https://api-iam.eu.intercom.io"
      onHide={onHide}
      onShow={onShow}
    >
      <CustomIntercomStateContext.Provider value={providerState}>
        <IntercomLauncherVisibilityController />
        {children}
      </CustomIntercomStateContext.Provider>
    </IntercomProvider>
  );
};
